import { MARKETING_PORTFOLIO_ROLES } from "./marketingAccess";

/** Roles that may create/edit sprints, manage epics, and use sprint steward UI on the board. */
export const SPRINT_STEWARD_ROLES = [
  "admin",
  "superadmin",
  "hod",
  "manager",
  "analyst",
  "product",
  "lead",
  "product_manager",
  ...MARKETING_PORTFOLIO_ROLES,
];

/** Settings → Role Permissions: view Planning sprints on the board (read-only; not sprint steward). */
export const SPRINT_PLANNING_VIEW_PERM = "sprint.planning.view";

export function isSprintSteward(role) {
  return SPRINT_STEWARD_ROLES.includes(String(role || "").toLowerCase());
}

/** Admin, manager, or HOD — may delete tasks, stories, and notebook entries. */
export function canDeleteWorkItems(role) {
  const r = String(role || "").toLowerCase();
  return ["superadmin", "admin", "manager", "hod"].includes(r);
}

/**
 * Admin, superadmin, or manager — may force a Tracker stage active without
 * waiting for its actual owner to accept. Narrower than isSprintSteward: this
 * overrides someone else's personal commitment, not routine stewardship, so
 * it mirrors CAN_FORCE_START_ROLES in trackerController.js exactly.
 */
export function canForceStartStage(role) {
  return ["admin", "superadmin", "manager"].includes(String(role || "").toLowerCase());
}

/**
 * Planning sprints are the team's shared plan, so everyone on the project
 * sees them — that is the whole point of promoting a draft out of Backlog.
 *
 * The sprint.planning.view permission predates Backlog, when Planning was
 * itself the private state and had to be opened up role by role. Backlog now
 * holds that privacy, so the permission no longer gates anything; it stays
 * exported and still returns true so any saved role grant keeps working.
 */
export function canViewPlanningSprints(_role, _permissions = []) {
  return true;
}

/** Backlog sprints are steward-only drafts; every other status is team-visible. */
export function isDraftSprint(sprint) {
  return String(sprint?.status || "") === "Backlog";
}

/**
 * Sprint statuses that accept work, mirroring acceptsWork() in
 * backend/constants/sprintLifecycle.js. Keep the two in step — the backend is
 * what actually rejects a bad target, so a wider list here only produces
 * options that 400.
 */
export const SPRINT_WORK_STATUSES = ["Backlog", "Planning", "Active"];

export function filterSprintsForBoardCarousel(sprints, role, _permissions) {
  const list = sprints || [];
  if (isSprintSteward(role)) return list;
  // Everything the team shares: Planning, Active, and the finished ones they
  // may still want to look back at. Only Backlog drafts are withheld.
  return list.filter((s) => !isDraftSprint(s));
}

/**
 * Target sprints for new work. The backend accepts Planning and Active only
 * (see moveToSprint in issueController.js), so offering anything else here
 * would hand the user an option that 400s. A steward additionally sees their
 * own Backlog drafts, which the backend also accepts.
 */
export function filterSprintsForIssuePicker(sprints, role, _permissions) {
  const list = sprints || [];
  if (isSprintSteward(role)) {
    return list.filter((s) => SPRINT_WORK_STATUSES.includes(s.status));
  }
  return list.filter((s) => SPRINT_WORK_STATUSES.includes(s.status) && !isDraftSprint(s));
}
